import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Grid } from '@elfsight/lib-masonry-grid';
import { createReadableClass, useIsMobile } from '@elfsight/widget-utils';
import type { ButtonProps } from '../../../button';
import { LoadMoreButton } from '../LoadMoreButton';
import type {
  ColumnsModes,
  DataSourceProps,
  LayoutAPI,
  MappingFunction
} from '../types';
import { transformDataSource, useColumns, useLayoutData } from '../utils';

export type GridLayoutProps = {
  renderItem: MappingFunction;
  dataSource: DataSourceProps;
  columnsMode: ColumnsModes;
  columnsDesktopLarge: number;
  columnsDesktopMedium: number;
  columnsDesktopSmall: number;
  columnsTablet: number;
  columnsMobile: number;
  itemSpacing: number;
  columnBasis?: number;
  rows?: number;
  rowsOnMobile?: number;
  loaderColor?: string;
  showLoadMoreButton?: boolean;
  loadMoreButtonText?: string;
  loadMoreButton?: ButtonProps;
  withPagination?: boolean;
  className?: string;
  onEffectiveColumnsChange?: (columnsCount: number) => void;
};

export const GridLayout = forwardRef<LayoutAPI, GridLayoutProps>(
  (
    {
      renderItem,
      columnBasis = 240,
      withPagination = false,
      dataSource,
      columnsMode,
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile,
      rows,
      rowsOnMobile,
      itemSpacing,
      loaderColor,
      showLoadMoreButton,
      loadMoreButtonText,
      loadMoreButton,
      className,
      onEffectiveColumnsChange
    },
    forwardingRef
  ) => {
    const isMobile = useIsMobile();
    const dataSourceFormat = transformDataSource(dataSource);

    const {
      data: initialData,
      hasNext: hasNextFetch,
      next: handleNextFetch,
      status
    } = dataSourceFormat;

    const isLoading = status === 'pending';

    const {
      recalculateColumns,
      autoColumns,
      customColumns,
      windowWidth,
      handleEffectiveColumnsChange
    } = useColumns({
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile
    });

    const columnsCount = columnsMode === 'auto' ? autoColumns : customColumns;

    const rowsCount = isMobile ? rowsOnMobile : rows;

    const itemsPerPage: number =
      withPagination && rowsCount
        ? rowsCount * columnsCount
        : initialData?.length;

    const {
      data,
      hasNext,
      loadNext,
      reset: resetLayout
    } = useLayoutData({
      data: initialData,
      itemsCountPerPage: itemsPerPage,
      hasNextFetch,
      handleNextFetch
    });

    useImperativeHandle(forwardingRef, () => ({ resetLayout }), [resetLayout]);

    const effectiveColumnsChangeHandler = (columnsCount: number) => {
      onEffectiveColumnsChange?.(columnsCount);
      handleEffectiveColumnsChange(columnsCount);
    };

    useEffect(() => {
      resetLayout();
    }, [showLoadMoreButton]);

    useEffect(() => {
      recalculateColumns();
    }, [
      columnsDesktopLarge,
      columnsDesktopMedium,
      columnsDesktopSmall,
      columnsTablet,
      columnsMobile,
      rowsOnMobile,
      rows,
      windowWidth
    ]);

    return (
      <>
        <Grid
          columns={columnsMode === 'auto' ? 'auto' : columnsCount}
          basis={columnBasis}
          gap={itemSpacing}
          onEffectiveColumnsChange={effectiveColumnsChangeHandler}
          className={createReadableClass(
            {
              component: 'grid-layout'
            },
            [className]
          )}
        >
          {data.map(renderItem)}
        </Grid>
        {withPagination && showLoadMoreButton && (
          <LoadMoreButton
            hasNext={hasNext}
            next={loadNext}
            isLoading={isLoading}
            text={loadMoreButtonText}
            gap={itemSpacing}
            loaderColor={loaderColor}
            loadMoreButton={loadMoreButton}
          />
        )}
      </>
    );
  }
);
